import styled, { keyframes } from 'styled-components';
import { fadeInUp, pulse } from '../shared/styles/animations';

// ========================================
// Animations
// ========================================

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;


// ========================================
// Main Container
// ========================================

export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 4rem;
`;

// ========================================
// Banner Section
// ========================================

export const BannerSection = styled.section`
  background: url(${props => props.$backgroundImage || ''}) center center;
  background-size: cover;
  background-position: center;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: relative;
  min-height: 160px;
`;

// ========================================
// Content Section
// ========================================

export const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

// ========================================
// Games Grid
// ========================================

export const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

export const GameCard = styled.div`
  position: relative;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 3px solid transparent;
  animation: ${scaleIn} 0.5s ease-out both;
  
  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  
  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
  
  &:nth-child(4) {
    animation-delay: 0.4s;
  }
  
  /* Borde con gradiente */
  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    background: linear-gradient(135deg, #D97D2B, #1E4C5C, #D97D2B);
    border-radius: 20px;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 20px 50px rgba(217, 125, 43, 0.3);
  }
  
  &:hover::before {
    opacity: 0.6;
  }
  
  &:active {
    transform: translateY(-5px) scale(1);
  }
  
  /* En mobile, mantener el efecto hover pero más sutil */
  @media (max-width: 768px) {
    &:active {
      transform: translateY(-3px) scale(0.98);
    }
  }
  
  @media (max-width: 480px) {
    border-radius: 16px;
    
    &::before {
      border-radius: 16px;
    }
    
    &:active {
      transform: translateY(-2px) scale(0.98);
    }
  }
`;

export const GameImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%; /* Aspect ratio 1:1 */
  overflow: hidden;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
`;

export const GameImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${GameCard}:hover & {
    transform: scale(1.1);
  }
`;

export const GameOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.3) 50%,
    rgba(0, 0, 0, 0.6) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${GameCard}:hover & {
    opacity: 1;
  }
  
  /* En mobile, mostrar el overlay al tocar */
  @media (max-width: 768px) {
    ${GameCard}:active & {
      opacity: 0.8;
    }
  }
`;

export const TikTokIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  box-shadow: 0 8px 25px rgba(217, 125, 43, 0.5);
  animation: ${pulse} 2s ease-in-out infinite;
  transform: scale(0.9);
  transition: all 0.3s ease;
  
  svg {
    width: 1.2em;
    height: 1.2em;
  }
  
  ${GameCard}:hover & {
    transform: scale(1);
    background: linear-gradient(135deg, #000000 0%, #FE2C55 100%);
    box-shadow: 0 8px 25px rgba(254, 44, 85, 0.5);
  }
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
    
    svg {
      width: 1em;
      height: 1em;
    }
  }
  
  @media (max-width: 480px) {
    width: 45px;
    height: 45px;
    font-size: 1.3rem;
    
    svg {
      width: 0.9em;
      height: 0.9em;
    }
  }
`;

